import { addDays, format, getDay, parseISO } from 'date-fns';
import ruLocale from 'date-fns/locale/ru';

const FIXED_HOLIDAY_NAMES = {
  '01-01': 'Новогодние каникулы',
  '01-02': 'Новогодние каникулы',
  '01-03': 'Новогодние каникулы',
  '01-04': 'Новогодние каникулы',
  '01-05': 'Новогодние каникулы',
  '01-06': 'Новогодние каникулы',
  '01-07': 'Рождество Христово',
  '01-08': 'Новогодние каникулы',
  '02-23': 'День защитника Отечества',
  '03-08': 'Международный женский день',
  '05-01': 'Праздник Весны и Труда',
  '05-09': 'День Победы',
  '06-12': 'День России',
  '11-04': 'День народного единства',
};

const FIXED_HOLIDAY_MD = new Set(Object.keys(FIXED_HOLIDAY_NAMES));

const YEAR_CONFIGS = {
  2024: {
    transfers: [
      { from: '2024-01-06', to: '2024-05-10' },
      { from: '2024-01-07', to: '2024-12-31' },
      { from: '2024-04-27', to: '2024-04-29' },
      { from: '2024-11-02', to: '2024-04-30' },
      { from: '2024-12-28', to: '2024-12-30' },
    ],
    shortDays: ['2024-05-08', '2024-06-11', '2024-11-01'],
  },
  2025: {
    transfers: [
      { from: '2025-01-04', to: '2025-05-02' },
      { from: '2025-01-05', to: '2025-12-31' },
      { from: '2025-02-23', to: '2025-05-08' },
      { from: '2025-03-08', to: '2025-06-13' },
      { from: '2025-11-01', to: '2025-11-03' },
    ],
    shortDays: ['2025-03-07', '2025-04-30', '2025-06-11'],
  },
  2026: {
    transfers: [
      { from: '2026-01-03', to: '2026-01-09' },
      { from: '2026-01-04', to: '2026-12-31' },
    ],
    shortDays: ['2026-04-30', '2026-05-08', '2026-06-11', '2026-11-03'],
  },
  2027: {
    transfers: [
      { from: '2027-01-02', to: '2027-11-05' },
      { from: '2027-01-03', to: '2027-12-31' },
      { from: '2027-02-20', to: '2027-02-22' },
    ],
    shortDays: ['2027-04-30', '2027-05-07', '2027-06-11', '2027-11-03'],
    preliminary: true,
  },
  2028: {
    transfers: [
      { from: '2028-01-01', to: '2028-05-08' },
      { from: '2028-11-04', to: '2028-11-06' },
    ],
    shortDays: ['2028-04-28', '2028-06-09', '2028-11-03'],
    preliminary: true,
  },
};

function isWeekend(date) {
  const dow = getDay(parseISO(date));
  return dow === 0 || dow === 6;
}

function isFixedHoliday(date) {
  return FIXED_HOLIDAY_MD.has(date.slice(5));
}

function formatTransferDate(date) {
  return format(parseISO(date), 'd MMMM yyyy', { locale: ruLocale });
}

function resolveDayType(date, isOff, isShort, isTransferTarget) {
  if (isShort && !isOff) {
    return 'short_day';
  }
  if (!isOff) {
    return 'working';
  }
  if (isFixedHoliday(date) || isTransferTarget || !isWeekend(date)) {
    return 'holiday';
  }
  return 'weekend';
}

export function buildRussianProductionCalendar(year) {
  const config = YEAR_CONFIGS[year];
  if (!config) {
    return {
      days: [],
      stats: { working: 0, weekend: 0, holiday: 0, short_day: 0 },
      preliminary: false,
    };
  }

  const transferSources = new Set(config.transfers.map((t) => t.from));
  const transferTargets = new Set(config.transfers.map((t) => t.to));
  const shortDays = new Set(config.shortDays);

  const stats = { working: 0, weekend: 0, holiday: 0, short_day: 0 };
  const days = [];
  let cursor = parseISO(`${year}-01-01`);
  const end = parseISO(`${year}-12-31`);

  while (cursor <= end) {
    const date = format(cursor, 'yyyy-MM-dd');
    let isOff = isWeekend(date) || isFixedHoliday(date);

    if (transferTargets.has(date)) {
      isOff = true;
    }
    if (transferSources.has(date) && !isFixedHoliday(date)) {
      isOff = false;
    }

    const isShort = shortDays.has(date);
    const type = resolveDayType(date, isOff, isShort, transferTargets.has(date));

    days.push({
      date,
      type,
      weekday: getDay(cursor),
    });
    stats[type] += 1;
    cursor = addDays(cursor, 1);
  }

  return {
    days,
    stats,
    preliminary: Boolean(config.preliminary),
  };
}

export function getRussianProductionCalendarDayDescription(day) {
  const year = parseISO(day.date).getFullYear();
  const config = YEAR_CONFIGS[year];
  const fixedHolidayName = FIXED_HOLIDAY_NAMES[day.date.slice(5)];
  const transferTo = config?.transfers.find((t) => t.to === day.date);
  const transferFrom = config?.transfers.find((t) => t.from === day.date);

  if (fixedHolidayName) {
    return fixedHolidayName;
  }
  if (transferTo) {
    return `Перенесённый выходной с ${formatTransferDate(transferTo.from)}`;
  }
  if (day.type === 'short_day') {
    return 'Предпраздничный сокращённый рабочий день';
  }
  if (transferFrom && day.type === 'working') {
    return `Рабочий день из-за переноса выходного на ${formatTransferDate(
      transferFrom.to,
    )}`;
  }
  if (day.type === 'weekend') {
    return getDay(parseISO(day.date)) === 6 ? 'Суббота' : 'Воскресенье';
  }
  if (day.type === 'holiday') {
    return 'Нерабочий праздничный день';
  }
  return 'Обычный рабочий день';
}
